import { createSlice } from "@reduxjs/toolkit";
import {
    getContracts,
    addNewContract,
    createContract,
    updateContract,
    deleteContract,
    getContractById,
    getContractByIdFull,
} from "./thunk";

export const initialState = {
    contracts: [],
    clientContracts: [],
    error: null,
    selectedContract: null,
    selectedContractFull: null,
    contract: null,
    isContractSuccess: false,
    isContractCreated: false,
    loading: false,
};
import { updatePaymentBulk } from "../payments/thunk";

const contractsSlice = createSlice({
    name: "ContractsSlice",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        // Fetch contracts
        builder.addCase(getContracts.fulfilled, (state, action) => {
            state.contracts = action.payload;
            state.isContractSuccess = true;
            state.error = null;
            state.loading = false;
        });

        builder.addCase(getContracts.rejected, (state, action) => {
            state.error = action.payload?.error || "Failed to fetch contracts";
            state.isContractSuccess = false;
        });

        builder.addCase(getContracts.pending, (state) => {
            state.loading = true;
            state.error = null;
        });

        // Fetch a single contract by ID
        builder.addCase(getContractById.fulfilled, (state, action) => {
            state.selectedContract = action.payload;
            state.error = null;
        });

        builder.addCase(getContractById.rejected, (state, action) => {
            state.error = action.payload?.error || "Failed to fetch contract by ID";
            state.selectedContract = null;
        });

        // Fetch a full contract by ID (with all details)
        builder.addCase(getContractByIdFull.pending, (state) => {
            state.loading = true;
            state.error = null;
        });

        builder.addCase(getContractByIdFull.fulfilled, (state, action) => {
            const fullContractData = action.payload;
            state.selectedContractFull = fullContractData;
            state.contract = {
                id: fullContractData.id,
                pvc_number: fullContractData.pvc_number,
                pvc_date: fullContractData.pvc_date,
                notary: fullContractData.notary,
                notes: fullContractData.notes,
                type: fullContractData.type
            };
            state.error = null;
            state.loading = false;
        });

        builder.addCase(getContractByIdFull.rejected, (state, action) => {
            state.error = action.payload?.error || "Failed to fetch full contract details";
            state.selectedContractFull = null;
            contract = null;
            state.loading = false;
        });

        // Add new contract
        builder.addCase(addNewContract.fulfilled, (state, action) => {
            state.contracts.push(action.payload);
            state.clientContracts.push(action.payload);
            state.isContractCreated = true;
            state.error = null;
        });

        builder.addCase(addNewContract.rejected, (state, action) => {
            state.error = action.payload || "Failed to add contract";
            state.isContractCreated = false;
        });

        // Create contract
        builder.addCase(createContract.fulfilled, (state, action) => {
            state.contracts.push(action.payload);
            state.clientContracts.push(action.payload);
            state.isContractCreated = true;
            state.error = null;
        });

        builder.addCase(createContract.rejected, (state, action) => {
            state.error = action.payload || "Failed to add contract";
            state.isContractCreated = false;
        });

        builder.addCase(updatePaymentBulk.fulfilled, (state, action) => {
            if (state.selectedContractFull && action.payload?.success && action.payload?.data) {
                const { payments, updated_units, contract_summary } = action.payload.data;
                
                if (updated_units && Array.isArray(updated_units)) {
                updated_units.forEach(updatedUnit => {
                    const unitIndex = state.selectedContractFull.units.findIndex(u => u.id === updatedUnit.id);
                    if (unitIndex !== -1) {
                    if (updatedUnit.settlement_price) {
                        state.selectedContractFull.units[unitIndex].settlement_price = {
                        ...state.selectedContractFull.units[unitIndex].settlement_price,
                        ...updatedUnit.settlement_price
                        };
                    }
                    
                    const unitPayments = payments.filter(p => p.unit_id === updatedUnit.id);
                    if (unitPayments.length > 0) {
                        state.selectedContractFull.units[unitIndex].settlement_price.payments = unitPayments.map(payment => ({
                        id: payment.id,
                        type: payment.type,
                        amount: payment.amount,
                        cashed: payment.cashed,
                        booking_date: payment.booking_date,
                        cashed_date: payment.cashed_date,
                        vat_percentage: payment.vat_percentage,
                        tax: payment.tax
                        }));
                    }
                    }
                });
                }
                
                if (contract_summary) {
                if (contract_summary.total_cashed !== undefined) {
                    state.selectedContractFull.total_cashed = contract_summary.total_cashed;
                }
                if (contract_summary.total_contract_value !== undefined) {
                    state.selectedContractFull.total_contract_value = contract_summary.total_contract_value;
                }
                if (contract_summary.remaining_balance !== undefined) {
                    state.selectedContractFull.remaining_balance = contract_summary.remaining_balance;
                }
                }
            }
            else if (state.selectedContractFull && Array.isArray(action.payload)) {
                const updatedPayments = action.payload;
                
                const paymentsBySettlementPrice = {};
                updatedPayments.forEach(payment => {
                const settlementPriceId = payment.settlement_price;
                if (settlementPriceId) {
                    if (!paymentsBySettlementPrice[settlementPriceId]) {
                    paymentsBySettlementPrice[settlementPriceId] = [];
                    }
                    paymentsBySettlementPrice[settlementPriceId].push(payment);
                }
                });

                state.selectedContractFull.units = state.selectedContractFull.units.map(unit => {
                if (unit.settlement_price && paymentsBySettlementPrice[unit.settlement_price.id]) {
                    const newPayments = paymentsBySettlementPrice[unit.settlement_price.id];
                    
                    unit.settlement_price.payments = newPayments.map(payment => ({
                    id: payment.id,
                    type: payment.type,
                    amount: payment.amount,
                    cashed: payment.cashed,
                    booking_date: payment.booking_date,
                    cashed_date: payment.cashed_date,
                    vat_percentage: payment.vat_percentage,
                    tax: payment.tax
                    }));

                    unit.settlement_price.total_cashed = newPayments.reduce((sum, payment) => {
                    const cashedAmount = payment.cashed !== null && parseFloat(payment.cashed || 0) > 0 
                        ? parseFloat(payment.cashed || 0) 
                        : 0;
                    return sum + cashedAmount;
                    }, 0);
                }
                return unit;
                });

                state.selectedContractFull.total_cashed = state.selectedContractFull.units.reduce((total, unit) => {
                return total + (unit.settlement_price?.total_cashed || 0);
                }, 0);
            }
        });

        builder.addCase(updatePaymentBulk.rejected, (state, action) => {
            state.error = action.payload?.error || "Failed to update payment";
        });

        // Update contract
        builder.addCase(updateContract.fulfilled, (state, action) => {
            // Update the contract in the contracts array
            state.contracts = state.contracts.map((contract) =>
                contract.id === action.payload.id
                    ? { ...contract, ...action.payload }
                    : contract
            );
            
            // If we're updating the currently selected contract, update it too
            if (state.selectedContract && state.selectedContract.id === action.payload.id) {
                state.selectedContract = { ...state.selectedContract, ...action.payload };
            }
            
            // If we're updating the currently selected full contract, update its basic fields
            if (state.selectedContractFull && state.selectedContractFull.id === action.payload.id) {
                state.selectedContractFull = { 
                    ...state.selectedContractFull, 
                    ...action.payload 
                };
            }
            
            state.error = null;
        });

        builder.addCase(updateContract.rejected, (state, action) => {
            state.error = action.payload?.error || "Failed to update contract";
        });

        // Delete contract
        builder.addCase(deleteContract.fulfilled, (state, action) => {
            state.contracts = state.contracts.filter(
                (contract) => contract.id !== action.payload.contractId
            );
            
            // Clear selected contracts if they were deleted
            if (state.selectedContract && state.selectedContract.id === action.payload.contractId) {
                state.selectedContract = null;
            }
            
            if (state.selectedContractFull && state.selectedContractFull.id === action.payload.contractId) {
                state.selectedContractFull = null;
            }
            
            state.error = null;
        });

        builder.addCase(deleteContract.rejected, (state, action) => {
            state.error = action.payload?.error || "Failed to delete contract";
        });
    },
});

export default contractsSlice.reducer;